
import { useState, useMemo } from 'react';
import { useCrmData } from '../../../contexts/CrmProviders';
import { useTags } from '../../settings/tags/hooks/useTags';
import { ChatConversation } from './types';
import { notify } from '../../../utils/toast';
import { createModuleLogger } from '../../../utils/logger';

const log = createModuleLogger('[useChatHeader]');

interface UseChatHeaderProps {
    conversation: ChatConversation;
    selectedTagIds?: string[];
    onTransfer?: (userId: string) => void;
    onCloseConversation?: () => void;
    onReopenConversation?: () => void;
}

/**
 * Hook para gerenciar o estado e ações do ChatHeader.
 * 
 * Refatorado: Removida lógica de adicionar contato manual.
 * Contatos são salvos automaticamente pelo webhook quando cliente envia mensagem.
 */
export const useChatHeader = ({
    conversation,
    selectedTagIds = [],
    onTransfer,
    onCloseConversation,
    onReopenConversation
}: UseChatHeaderProps) => {
    // 1. Estados Locais de UI (Modais)
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

    // 2. Dados de Contexto
    const { crmUsers } = useCrmData();
    const { tags } = useTags();

    // 3. Computed Data (Derivados)
    const selectedTags = useMemo(() =>
        tags.filter(tag => selectedTagIds.includes(tag.id)),
        [tags, selectedTagIds]
    );

    const isClosed = conversation.status === 'closed';

    // 4. Handlers de Ação
    const handleTransfer = async (userId: string) => {
        if (onTransfer) {
            await onTransfer(userId);
        }
        setIsTransferModalOpen(false);
    };

    const handleCloseConversation = () => {
        if (!onCloseConversation) return;

        notify.warning('Deseja realmente encerrar esta conversa?', {
            duration: 5000,
            action: {
                label: 'Encerrar',
                onClick: () => {
                    onCloseConversation();
                    notify.success('Conversa encerrada com sucesso!');
                }
            },
            cancel: {
                label: 'Cancelar',
                onClick: () => {
                    notify.dismiss();
                }
            }
        });
    };

    const handleReopenConversation = async () => {
        if (!onReopenConversation) return;

        try {
            await onReopenConversation();
            notify.success('Conversa reaberta com sucesso!');
        } catch (error) {
            log.error('Erro ao reabrir conversa', error);
            notify.error('Erro ao reabrir conversa');
        }
    };

    return {
        // State
        isTransferModalOpen,
        setIsTransferModalOpen,

        // Data
        crmUsers,
        selectedTags,
        isClosed,

        // Actions
        handleTransfer,
        handleCloseConversation,
        handleReopenConversation
    };
};

